import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllTeachers,
  getTeacherById,
  createTeacher,
  updateTeacher,
  deleteTeacher
} from '../services/teacherService';
import Notification from '../components/Common/Notification';

export const TeacherContext = createContext();

export const TeacherProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllTeachers = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllTeachers(authHeaders);
      setTeachers(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching teachers.';
      showNotification(errorMessage, true);
    }
  };

  const fetchTeacherById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherById(id, authHeaders);
      setSelectedTeacher(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching teacher with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addTeacher = async (teacherData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newTeacher = await createTeacher(teacherData, authHeaders);
      setTeachers(prevTeachers => [...prevTeachers, newTeacher]);
      showNotification('Teacher added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding teacher.';
      showNotification(errorMessage, true);
    }
  };

  const editTeacher = async (id, teacherData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedTeacher = await updateTeacher(id, teacherData, authHeaders);
      setTeachers(prevTeachers =>
        prevTeachers.map(teacher => (teacher.id === id ? updatedTeacher : teacher))
      );
      showNotification('Teacher updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating teacher.';
      showNotification(errorMessage, true);
    }
  };

  const removeTeacher = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteTeacher(id, authHeaders);
      setTeachers(prevTeachers => prevTeachers.filter(teacher => teacher.id !== id));
      showNotification('Teacher removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing teacher.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllTeachers();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <TeacherContext.Provider
      value={{
        teachers,
        selectedTeacher,
        fetchAllTeachers,
        fetchTeacherById,
        addTeacher,
        editTeacher,
        removeTeacher,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </TeacherContext.Provider>
  );
};
